<template>
  <VChip
    id="consultation-type-chip"
    density="compact"
    :variant="variant"
    :color="color"
    :rounded="!fullRounded ? 'md' : 'xs'"
    :class="['shrink-0 font-semibold text-sm/[20] pl-2 pr-2', ...chipClasses]"
  >
    <span :class="chipContentClasses">
      {{ translateUrgencyDTO(consultationType, t, shortLabel) }}
      <span v-if="count">({{ count }})</span>
    </span>

    <template v-if="closable" #close>
      <VIcon icon="close" density="compact" size="14" @click="emit('close')" />
    </template>
  </VChip>
</template>

<script setup lang="ts">
import { UrgencyDTO } from '@/generated'
import { translateUrgencyDTO } from '@/util/translations'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

interface Emits {
  (e: 'close'): void
}

const {
  consultationType,
  useColors = true,
  dark = false,
  shortLabel = false,
  variant = 'flat',
  count,
  fullRounded = true
} = defineProps<{
  consultationType: UrgencyDTO
  shortLabel?: boolean
  useColors?: boolean
  dark?: boolean
  variant?: ChipVariant
  count?: number
  fullRounded?: boolean
  closable?: boolean
}>()
const { t } = useI18n()
const emit = defineEmits<Emits>()

type ChipVariant = 'flat' | 'outlined'
type ChipColor =
  | 'warning'
  | 'warning-700'
  | 'grey-300'
  | 'error'
  | 'text-primary-text'
  | 'primary-700'
  | 'error-700'

const color = computed<ChipColor>(() => {
  if (useColors && consultationType === UrgencyDTO.EMERGENCY) {
    return dark ? 'error-700' : 'error'
  }
  if (useColors && consultationType === UrgencyDTO.URGENT) {
    return dark ? 'warning-700' : 'warning'
  }
  if (useColors && consultationType === UrgencyDTO.ROUTINE) {
    return 'primary-700'
  }

  return dark ? 'text-primary-text' : 'grey-300'
})

const chipOutlinedStyles = computed(() => {
  switch (consultationType) {
    case UrgencyDTO.EMERGENCY:
      return 'bg-error-50 border-[#FECDCA]'
    case UrgencyDTO.URGENT:
      return 'bg-warning-50 border-[#FEDF89]'
    case UrgencyDTO.ROUTINE:
    default:
      return 'bg-primary-50 border-[#8EBAD4]'
  }
})

const chipClasses = computed<string[]>(() => {
  if (
    useColors &&
    (consultationType === UrgencyDTO.EMERGENCY ||
      consultationType === UrgencyDTO.URGENT ||
      consultationType === UrgencyDTO.ROUTINE)
  ) {
    return [variant === 'outlined' ? chipOutlinedStyles.value : 'text-surface']
  }

  return [dark ? 'bg-[#206583]' : 'text-primary']
})

const chipContentClasses = computed<string[]>(() => {
  if (
    useColors &&
    (consultationType === UrgencyDTO.EMERGENCY ||
      consultationType === UrgencyDTO.URGENT ||
      consultationType === UrgencyDTO.ROUTINE)
  ) {
    return []
  }

  return [dark ? 'text-surface-text' : 'text-primary-text']
})
</script>

<style scoped>
:deep(.v-chip__close) {
  margin-inline-start: 2px !important;
  margin-inline-end: 0 !important;
}
</style>
